import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Building2, UserRound, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Field, CompanyFields, emptyCompany, validateCompany, formToCompany } from '@/components/forms/CompanyFields';
import { PasswordInput, validateAccount, TermsCheckbox } from './AuthPages';
import { authService, companyService } from '@/services/api';
import { useData } from '@/hooks/useData';
import { useSeo } from '@/lib/seo';

const Step = ({ n, icon: Icon, title, children }) => (
  <section className="surface p-5 sm:p-6">
    <div className="mb-5 flex items-center gap-3">
      <span className="grid h-8 w-8 place-items-center rounded-lg border border-cyan-300/30 bg-cyan-300/10 font-mono text-xs text-cyan-100">{n}</span>
      <h2 className="flex items-center gap-2 font-display text-lg font-semibold"><Icon className="h-4 w-4 text-primary" /> {title}</h2>
    </div>
    {children}
  </section>
);

export default function RegisterCompany() {
  useSeo({ title: 'Cadastrar empresa' });
  const { user, myCompany, authLoading } = useData();
  const [account, setAccount] = useState({ name: '', email: '', password: '', confirm: '' });
  const [company, setCompany] = useState(emptyCompany);
  const [terms, setTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  if (authLoading) {
    return <div className="container-x py-16 text-center text-sm text-muted-foreground">Carregando…</div>;
  }

  if (user && myCompany) {
    return (
      <div className="container-x py-16">
        <div className="surface mx-auto max-w-lg p-8 text-center" data-testid="register-already">
          <CheckCircle2 className="mx-auto h-8 w-8 text-primary" />
          <h1 className="mt-3 text-xl font-semibold">Sua empresa já está cadastrada</h1>
          <p className="mt-2 text-sm text-muted-foreground">{myCompany.name} está vinculada à sua conta.</p>
          <div className="mt-5 flex justify-center gap-2">
            <Button asChild className="btn-glow"><Link to="/painel/perfil">Editar perfil</Link></Button>
            <Button asChild variant="secondary" className="border border-white/10"><Link to={`/empresas/${myCompany.id}`}>Ver perfil público</Link></Button>
          </div>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (!user) Object.entries(validateAccount(account)).forEach(([k, v]) => { er[`acc_${k}`] = v; });
    Object.assign(er, validateCompany(company));
    if (!terms) er.terms = 'É necessário aceitar os termos de uso e a política de privacidade.';
    setErrors(er);
    if (Object.keys(er).length) {
      toast.error('Revise os campos destacados.');
      setTimeout(() => document.querySelector('[aria-invalid="true"], [role="alert"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
      return;
    }
    setBusy(true);
    try {
      if (!user) await authService.signup(account);
      await companyService.create({ ...formToCompany(company), services: [], products: [] });
      toast.success('Cadastro salvo', { description: 'Sua empresa foi enviada. Ela aparece no diretório quando estiver ativa.' });
      // Recarrega o painel para carregar a empresa recém-criada
      window.location.assign('/painel');
    } catch (ex) {
      setErrors((p) => ({ ...p, acc_email: ex.message }));
      toast.error(ex.message);
      setBusy(false);
    }
  };

  const setAcc = (k) => (e) => setAccount({ ...account, [k]: e.target.value });

  return (
    <div className="container-x max-w-3xl py-10 sm:py-14">
      <div className="eyebrow">Cadastro gratuito</div>
      <h1 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">Cadastrar empresa</h1>
      <p className="mt-2 text-sm text-muted-foreground">Coloque seu negócio no mapa da rede. Campos com <span className="text-primary">*</span> são obrigatórios.</p>
      <form onSubmit={submit} noValidate className="mt-6 space-y-5" data-testid="register-company-form">
        {!user ? (
          <Step n="1" icon={UserRound} title="Responsável">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field id="reg-name-input" label="Nome do responsável" required error={errors.acc_name} className="sm:col-span-2"><Input id="reg-name-input" autoComplete="name" value={account.name} onChange={setAcc('name')} aria-invalid={!!errors.acc_name} data-testid="reg-name-input" /></Field>
              <Field id="reg-email-input" label="E-mail" required error={errors.acc_email} className="sm:col-span-2"><Input id="reg-email-input" type="email" autoComplete="email" value={account.email} onChange={setAcc('email')} aria-invalid={!!errors.acc_email} data-testid="reg-email-input" /></Field>
              <Field id="reg-password-input" label="Senha" required error={errors.acc_password} hint="Mínimo de 8 caracteres, com letras e números."><PasswordInput id="reg-password-input" autoComplete="new-password" value={account.password} onChange={setAcc('password')} invalid={!!errors.acc_password} testId="reg-password-input" /></Field>
              <Field id="reg-confirm-input" label="Confirmar senha" required error={errors.acc_confirm}><PasswordInput id="reg-confirm-input" autoComplete="new-password" value={account.confirm} onChange={setAcc('confirm')} invalid={!!errors.acc_confirm} testId="reg-confirm-input" /></Field>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">Já tem conta? <Link to="/entrar" state={{ from: '/cadastrar-empresa' }} className="text-primary hover:underline">Entrar</Link></p>
          </Step>
        ) : (
          <div className="surface flex items-center gap-3 p-4 text-sm"><UserRound className="h-4 w-4 text-primary" /> Cadastrando como <span className="font-medium">{user.name}</span> <span className="text-muted-foreground">({user.email})</span></div>
        )}
        <Step n={user ? '1' : '2'} icon={Building2} title="Dados da empresa">
          <CompanyFields values={company} errors={errors} onChange={setCompany} />
        </Step>
        <div className="surface p-5">
          <TermsCheckbox checked={terms} onChange={setTerms} error={errors.terms} id="reg-terms" />
        </div>
        <div className="flex justify-end">
          <Button type="submit" size="lg" className="btn-glow" disabled={busy} data-testid="register-company-submit-button">
            {busy ? <Loader2 className="animate-spin" /> : <CheckCircle2 />} Concluir cadastro
          </Button>
        </div>
      </form>
    </div>
  );
}